import {
  AlertOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import { FloatButton, Tooltip, message } from "antd";

import {
  AssetDetailsDocument,
  AssetErrorHandleAllDocument,
  AssetErrorResolveAllDocument,
} from "../../graphql/graphql";
import { depromisify } from "../../utils/depromisify";

const HandleAssetErrorAllButton = ({ assetId }: { assetId: string }) => {
  const [mutation, { loading }] = useMutation(AssetErrorHandleAllDocument);

  const handleHandle = depromisify(async () => {
    const response = await mutation({
      variables: { assetId },
      refetchQueries: [AssetDetailsDocument],
    });

    if (response.data?.assetErrorHandleAll === "OK") {
      await message.success("All errors have been handled successfully");
    } else if (response.data?.assetErrorHandleAll === "ERROR") {
      await message.error("Failed to handle errors. Please try again.");
    } else {
      await message.warning(
        "Unexpected response. Please check the system status.",
      );
    }
  });

  return (
    <Tooltip title="Handle All Errors" placement="left">
      <FloatButton
        icon={loading ? <LoadingOutlined /> : <AlertOutlined />}
        onClick={handleHandle}
      />
    </Tooltip>
  );
};

const ResolveAssetErrorAllButton = ({ assetId }: { assetId: string }) => {
  const [mutation, { loading }] = useMutation(AssetErrorResolveAllDocument);

  const handleResolve = depromisify(async () => {
    const response = await mutation({
      variables: { assetId },
      refetchQueries: [AssetDetailsDocument],
    });

    if (response.data?.assetErrorResolveAll === "OK") {
      await message.success("All errors have been resolved successfully");
    } else if (response.data?.assetErrorResolveAll === "ERROR") {
      await message.error("Failed to resolve errors. Please try again.");
    } else {
      await message.warning(
        "Unexpected response. Please check the system status.",
      );
    }
  });

  return (
    <Tooltip title="Resolve All Asset Errors" placement="left">
      <FloatButton
        icon={loading ? <LoadingOutlined /> : <CheckCircleOutlined />}
        onClick={handleResolve}
      />
    </Tooltip>
  );
};

interface AssetErrorActionsAllProps {
  hasUnhandledErrors: boolean;
  hasUnresolvedErrors: boolean;
  assetId: string;
}

const AssetErrorActionsAll = ({
  hasUnhandledErrors,
  hasUnresolvedErrors,
  assetId,
}: AssetErrorActionsAllProps) => {
  if (!hasUnhandledErrors && !hasUnresolvedErrors) return null;
  return (
    <>
      {hasUnhandledErrors && <HandleAssetErrorAllButton assetId={assetId} />}
      {!hasUnhandledErrors && hasUnresolvedErrors && (
        <ResolveAssetErrorAllButton assetId={assetId} />
      )}
    </>
  );
};

export default AssetErrorActionsAll;
